const nrwServiceGroupId = [
  '1', '1.1',
  '2', '2.1',
  '3', '3.1',
  '4', '4.1',
  '5', '5.1',
  '6', '6.1',
  '7', '7.1', '7.2',
  '8', '8.1', '8.2', '8.3', '8.4',
  '9', '9.1',
  '10', '10.1',
  '11', '11.1',
  '12', '12.1', '12.2', '12.3',
  '13', '13.1', '13.2',
  '14', '14.1', '14.2', '14.3', '14.4', '14.5',
  '15', '15.1',
  '16', '16.1', '16.2', '16.3', '16.4', '16.5',
  '17', '17.1',
  '18', '18.1',
  '19', '19.1',
  '20', '20.1',
  '21', '21.1', '21.2', '21.3', '21.4',
  '22', '22.1', '22.2', '22.3',
  '23', '23.1', '23.2', '23.3',
  '24', '24.1', '24.2',
  '25', '25.1',
  '26', '26.1', '26.2', '26.3',
  '27', '27.1',
  '28', '28.1',
  '29', '29.1',
  '30', '30.1', '30.2', '30.3', '30.4', '30.5', '30.6',
  '31', '31.1', '31.2',
  '32', '32.1', '32.2'
] as const;

export type AysNrwServiceGroupId = typeof nrwServiceGroupId[number]

export class AysNrwServiceGroup {

  public id: AysNrwServiceGroupId;

  constructor(
    id: AysNrwServiceGroupId,
    public name: string
  ) {
    // Coerce to string: NRW service-area ids round-trip through JSON in
    // dashboards/filters and integer-shaped ids ("1", "2") may resurface as
    // raw numbers. Without this, isLeistungsbereich/-gruppe and parent() crash
    // with "this.id.includes is not a function".
    this.id = String(id) as AysNrwServiceGroupId;
  }

  public parent(): AysNrwServiceGroup | undefined {
    if (this.isLeistungsgruppe()) {
      const parentId = this.id.slice(0, this.id.indexOf('.'));
      return AYS_NRW_SERVICE_GROUPS.find(it => it.id === parentId);
    } else {
      return undefined
    }
  }

  public children(): AysNrwServiceGroup[] {
    if (this.isLeistungsbereich()) {
      return AYS_NRW_SERVICE_GROUPS
        .filter(it => it.isLeistungsgruppe())
        .filter(it => it.parent()?.id === this.id)
    } else {
      return []
    }
  }

  public isLeistungsbereich(): boolean {
    return !this.id.includes('.');
  }

  public isLeistungsgruppe(): boolean {
    return this.id.includes('.');
  }
}

export const AYS_NRW_SERVICE_GROUPS_DISPLAY_NAMES: Record<AysNrwServiceGroupId, string> = {
  "1": "Allgemeine Innere Medizin",
  "1.1": "Allgemeine Innere Medizin",
  "2": "Endokrinologie und Diabetologie",
  "2.1": "Komplexe Endokrinologie und Diabetologie",
  "3": "Gastroenterologie",
  "3.1": "Komplexe Gastroenterologie",
  "4": "Nephrologie",
  "4.1": "Komplexe Nephrologie",
  "5": "Pneumologie",
  "5.1": "Komplexe Pneumologie",
  "6": "Rheumatologie",
  "6.1": "Komplexe Rheumatologie",
  "7": "Hämatologie und Onkologie",
  "7.1": "Stammzelltransplantation",
  "7.2": "Leukämie und Lymphome",
  "8": "Kardiologie",
  "8.1": "EPU/Ablation",
  "8.2": "Interventionelle Kardiologie",
  "8.3": "Kardiale Devices",
  "8.4": "Minimalinvasive Herzklappeninvervention",
  "9": "Allgemeine Chirurgie",
  "9.1": "Allgemeine Chirurgie",
  "10": "Kinder- und Jugendchirurgie",
  "10.1": "Kinder- und Jugendchirurgie",
  "11": "Plastische und Rekonstruktive Chirurgie",
  "11.1": "Plastische und Rekonstruktive Chirurgie",
  "12": "Gefäßmedizin",
  "12.1": "Bauchaortenaneurysma",
  "12.2": "Carotis operativ/ interventionell",
  "12.3": "Komplexe periphere arterielle Gefäße",
  "13": "Herzchirurgie",
  "13.1": "Herzchirurgie",
  "13.2": "Herzchirurgie - Kinder und Jugendliche",
  "14": "Orthopädie und Unfallchirurgie",
  "14.1": "Endoprothetik Hüfte",
  "14.2": "Endoprothetik Knie",
  "14.3": "Revision Hüftendoprothese",
  "14.4": "Revision Knieendoprothese",
  "14.5": "Wirbelsäuleneingriffe",
  "15": "Thoraxchirurgie",
  "15.1": "Thoraxchirurgie",
  "16": "Viszeralchirurgie",
  "16.1": "Bariatrische Chirurgie",
  "16.2": "Lebereingriffe",
  "16.3": "Ösophaguseingriffe",
  "16.4": "Pankreaseingriffe",
  "16.5": "Tiefe Rektumeingriffe",
  "17": "Augenheilkunde",
  "17.1": "Augenheilkunde",
  "18": "Haut- und Geschlechtskrankheiten",
  "18.1": "Haut- und Geschlechtskrankheiten",
  "19": "MKG",
  "19.1": "MKG",
  "20": "Urologie",
  "20.1": "Urologie",
  "21": "Frauenheilkunde und Geburtshilfe",
  "21.1": "Allgemeine Frauenheilkunde",
  "21.2": "Ovarial-CA",
  "21.3": "Senologie",
  "21.4": "Geburten",
  "22": "Neonatologie",
  "22.1": "Perinataler Schwerpunkt",
  "22.2": "Perinatalzentrum Level 1",
  "22.3": "Perinatalzentrum Level 2",
  "23": "Kinder- und Jugendmedizin",
  "23.1": "Allgemeine Kinder- und Jugendmedizin",
  "23.2": "Kinder-Hämatologie und -Onkologie - Stammzelltransplantation",
  "23.3": "Kinder-Hämatologie und -Onkologie - Leukämie und Lymphome",
  "24": "HNO",
  "24.1": "HNO",
  "24.2": "Cochleaimplantate",
  "25": "Neurochirurgie",
  "25.1": "Neurochirurgie",
  "26": "Neurologie",
  "26.1": "Allgemeine Neurologie",
  "26.2": "Stroke Unit",
  "26.3": "Neuro-Frühreha (NNF, Phase B)",
  "27": "Geriatrie",
  "27.1": "Geriatrie",
  "28": "Intensivmedizin",
  "28.1": "Intensivmedizin",
  "29": "Palliativmedizin",
  "29.1": "Palliativmedizin",
  "30": "Transplantation solider Organe",
  "30.1": "Darmtransplantation",
  "30.2": "Herztransplantation",
  "30.3": "Lebertransplantation",
  "30.4": "Lungentransplantation",
  "30.5": "Nierentransplantation",
  "30.6": "Pankreastransplantation",
  "31": "Psychiatrie und Psychotherapie und Psychosomatische Medizin und Psychotherapie",
  "31.1": "Psychiatrie und Psychotherapie und Psychosomatische Medizin und Psychotherapie - vollstationär",
  "31.2": "Psychiatrie und Psychotherapie und Psychosomatische Medizin und Psychotherapie - teilstationär",
  "32": "Kinder- und Jugendpsychiatrie und -psychotherapie",
  "32.1": "Kinder- und Jugendpsychiatrie und -psychotherapie - vollstationär",
  "32.2": "Kinder- und Jugend-psychiatrie und -psychotherapie - teilstationär"
}

export const AYS_NRW_SERVICE_GROUPS: AysNrwServiceGroup[] = nrwServiceGroupId
  .map(it => new AysNrwServiceGroup(it, AYS_NRW_SERVICE_GROUPS_DISPLAY_NAMES[it]))
